import React, { useState } from 'react';
import { Settings, SlidersHorizontal, BookOpen, Shield, TrendingUp, MinusCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { usePayrollConfig } from '../../../hooks/usePayrollConfig';
import ParametrosTab from './tabs/ParametrosTab';
import ConceptosTab from './tabs/ConceptosTab';
import TasasTSSTab from './tabs/TasasTSSTab';
import TiposIngresosTab from './tabs/TiposIngresosTab';
import TiposDeduccionesTab from './tabs/TiposDeduccionesTab';

const TABS = [
  { id: 'parametros',  label: 'Parámetros',      icon: SlidersHorizontal, color: 'blue' },
  { id: 'conceptos',   label: 'Conceptos',        icon: BookOpen,          color: 'blue' },
  { id: 'tss',         label: 'TSS',              icon: Shield,            color: 'blue' },
  { id: 'ingresos',    label: 'Tipos Ingresos',   icon: TrendingUp,        color: 'blue' },
  { id: 'deducciones', label: 'Tipos Deducciones', icon: MinusCircle,      color: 'blue' }
];

const TAB_ACTIVE_CLASSES = {
  blue: 'border-blue-600 text-blue-600 bg-blue-50'
};

export default function PayrollConfigModule() {
  const [activeTab, setActiveTab] = useState('parametros');

  const {
    parametros,
    conceptos,
    tssRates,
    incomeTypes,
    deductionTypes,
    loading,
    error,
    loadAll,
    saveParametros,
    createConcepto,
    updateConcepto,
    deleteConcepto,
    saveTSSRates,
    createIncomeType,
    updateIncomeType,
    deleteIncomeType,
    createDeductionType,
    updateDeductionType,
    deleteDeductionType
  } = usePayrollConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const activeTabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Configuración de Nómina</h2>
            <p className="text-sm text-gray-500">
              Parámetros globales, tasas TSS, conceptos y catálogos de nómina
            </p>
          </div>
          <button
            onClick={loadAll}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Recargar"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors
                    ${isActive
                      ? TAB_ACTIVE_CLASSES[tab.color]
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'parametros' && (
            <ParametrosTab
              parametros={parametros}
              saveParametros={saveParametros}
            />
          )}
          {activeTab === 'conceptos' && (
            <ConceptosTab
              conceptos={conceptos}
              createConcepto={createConcepto}
              updateConcepto={updateConcepto}
              deleteConcepto={deleteConcepto}
            />
          )}
          {activeTab === 'tss' && (
            <TasasTSSTab
              tssRates={tssRates}
              saveTSSRates={saveTSSRates}
              parametros={parametros}
              saveParametros={saveParametros}
            />
          )}
          {activeTab === 'ingresos' && (
            <TiposIngresosTab
              incomeTypes={incomeTypes}
              createIncomeType={createIncomeType}
              updateIncomeType={updateIncomeType}
              deleteIncomeType={deleteIncomeType}
            />
          )}
          {activeTab === 'deducciones' && (
            <TiposDeduccionesTab
              deductionTypes={deductionTypes}
              createDeductionType={createDeductionType}
              updateDeductionType={updateDeductionType}
              deleteDeductionType={deleteDeductionType}
            />
          )}
        </div>
      </div>
    </div>
  );
}
